import { supabase, isConfigured } from './supabase.js';
import { authState } from './auth.js';
import courseDataFallback from './data/courseData.json';

const LOCAL_PROGRESS_KEY = 'wt_discipleship_progress';
const LOCAL_REFLECTIONS_KEY = 'wt_discipleship_reflections';
const LOCAL_QUIZZES_KEY = 'wt_discipleship_quizzes';

export const store = {
  course: courseDataFallback.course,
  modules: courseDataFallback.modules,
  lessons: courseDataFallback.lessons,
  quizzes: courseDataFallback.quizzes,
  
  // User state
  progress: {},       // lessonId -> { is_completed, verse_memorized, actions_completed, notes }
  reflections: {},    // lessonId -> { response_text, share_with_cohort, updated_at }
  quizAttempts: {},   // quizId -> { score, passed, answers, submitted_at }
  cohortReflections: [], // reflections from group members
  currentGroup: null, // active group metadata { id, name, invite_code, my_role }
  groupMembers: [],   // roster of members in the active group
  
  isLoaded: false,
  listeners: []
};

export function subscribeStore(callback) {
  store.listeners.push(callback);
  callback(store);
  return () => {
    store.listeners = store.listeners.filter(cb => cb !== callback);
  };
}

function notifyStore() {
  store.listeners.forEach(cb => cb(store));
}

// Load course catalog & user progress
export async function initStore() {
  // First, load local cache
  loadLocalState();

  // Try fetching from Supabase if configured
  if (isConfigured) {
    try {
      await fetchCatalogFromSupabase();
    } catch (err) {
      console.warn('Could not load catalog from Supabase, using bundled course data:', err.message);
    }

    if (authState.user) {
      await fetchUserProgressFromSupabase(authState.user.id);
    }
  }

  store.isLoaded = true;
  notifyStore();
}

async function fetchCatalogFromSupabase() {
  const { data: courses, error: cErr } = await supabase.from('courses').select('*').limit(1);
  if (cErr || !courses || courses.length === 0) return;

  const { data: modules, error: mErr } = await supabase.from('modules').select('*').order('order_index');
  const { data: lessons, error: lErr } = await supabase.from('lessons').select('*').order('overall_order');
  const { data: verses, error: vErr } = await supabase.from('memory_verses').select('*');
  const { data: quizzes, error: qErr } = await supabase.from('quizzes').select('*, quiz_questions(*)');

  if (lessons && lessons.length > 0) {
    store.course = courses[0];
    store.modules = modules || store.modules;

    // Attach modules, verses & quizzes
    const moduleMap = {};
    (store.modules || []).forEach(m => { moduleMap[m.id] = m; });

    const verseMap = {};
    (verses || []).forEach(v => { verseMap[v.lesson_id] = v; });

    const quizMap = {};
    (quizzes || []).forEach(q => { quizMap[q.lesson_id] = q; });

    store.lessons = lessons.map(l => {
      const v = verseMap[l.id];
      const q = quizMap[l.id];
      const m = moduleMap[l.module_id];
      const modName = m?.title || l.module_name || '';
      const pdfFilename = `Discipleship-Walk-Together-Ministries-Lesson-${l.overall_order}.pdf`;
      // Fallback: use local PDF if db record still has old wp-content URL (SQL migration handles this)
      const pdfUrl = (l.pdf_url && !l.pdf_url.includes('wp-content')) ? l.pdf_url : `/discipleship/${pdfFilename}`;
      // Fallback: rewrite any residual discipleship wp-content PDF links in overview content
      const overviewContent = l.overview_content ? l.overview_content.replace(
        /https:\/\/discipleship\.walktogetherministries\.com\/wp-content\/uploads\/[^"]+?\.pdf/g,
        pdfUrl
      ) : '';

      return {
        ...l,
        module_name: modName,
        pdf_url: pdfUrl,
        pdf_name: pdfFilename,
        overview_content: overviewContent || l.overview_content || '',
        verse_ref: v?.reference || '',
        verse_text: v?.verse_text || '',
        sections: {
          know: l.know_content || '',
          feel: l.feel_prompt || '',
          do: l.do_content || '',
          pray: l.pray_content || '',
          group_exercise: l.group_exercise || '',
          additional_resources: l.resources_content || ''
        },
        quiz: q
      };
    });

    if (quizzes && quizzes.length > 0) {
      store.quizzes = quizzes;
    }
  }
}

export async function fetchUserProgressFromSupabase(userId) {
  if (!userId || !isConfigured) return;

  try {
    // 1. Progress
    const { data: progList } = await supabase
      .from('lesson_progress')
      .select('*')
      .eq('user_id', userId);

    if (progList) {
      progList.forEach(p => {
        store.progress[p.lesson_id] = {
          is_completed: p.is_completed,
          verse_memorized: p.verse_memorized,
          actions_completed: p.actions_completed || [],
          notes: p.personal_notes || ''
        };
      });
    }

    // 2. Reflections
    const { data: refList } = await supabase
      .from('reflections')
      .select('*')
      .eq('user_id', userId);

    if (refList) {
      refList.forEach(r => {
        store.reflections[r.lesson_id] = {
          response_text: r.response_text,
          share_with_cohort: r.share_with_cohort,
          group_id: r.group_id,
          updated_at: r.updated_at
        };
      });
    }

    // 2b. Fetch User Group & Roster
    try {
      const { data: membership } = await supabase
        .from('group_members')
        .select('role, joined_at, groups (id, name, invite_code, description, leader_id)')
        .eq('user_id', userId)
        .order('joined_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (membership && membership.groups) {
        store.currentGroup = {
          ...membership.groups,
          my_role: membership.role,
          joined_at: membership.joined_at
        };

        // Fetch group members
        const { data: members } = await supabase
          .from('group_members')
          .select('role, joined_at, user_id, profiles (id, display_name, full_name, avatar_url)')
          .eq('group_id', membership.groups.id);

        if (members) {
          store.groupMembers = members;
        }
      }
    } catch (gErr) {
      console.warn('Could not fetch group membership:', gErr);
    }

    // 3. Group / Cohort shared reflections (scoped to current group if exists)
    let cohortQuery = supabase
      .from('reflections')
      .select('*, profiles(display_name, cohort_name)')
      .eq('share_with_cohort', true)
      .order('created_at', { ascending: false })
      .limit(30);

    if (store.currentGroup?.id) {
      cohortQuery = cohortQuery.eq('group_id', store.currentGroup.id);
    }

    const { data: cohortList } = await cohortQuery;

    if (cohortList) {
      store.cohortReflections = cohortList;
    }

    // 4. Quizzes
    const { data: qList } = await supabase
      .from('quiz_attempts')
      .select('*')
      .eq('user_id', userId);

    if (qList) {
      qList.forEach(q => {
        store.quizAttempts[q.quiz_id] = {
          score: q.score,
          passed: q.passed,
          answers: q.answers,
          submitted_at: q.submitted_at
        };
      });
    }

    notifyStore();
  } catch (err) {
    console.warn('Error fetching progress from Supabase:', err);
  }
}

function loadLocalState() {
  try {
    store.progress = JSON.parse(localStorage.getItem(LOCAL_PROGRESS_KEY) || '{}');
    store.reflections = JSON.parse(localStorage.getItem(LOCAL_REFLECTIONS_KEY) || '{}');
    store.quizAttempts = JSON.parse(localStorage.getItem(LOCAL_QUIZZES_KEY) || '{}');
  } catch (e) {
    console.warn('Error parsing local progress cache:', e);
  }
}

function saveLocalState() {
  localStorage.setItem(LOCAL_PROGRESS_KEY, JSON.stringify(store.progress));
  localStorage.setItem(LOCAL_REFLECTIONS_KEY, JSON.stringify(store.reflections));
  localStorage.setItem(LOCAL_QUIZZES_KEY, JSON.stringify(store.quizAttempts));
}

// User Actions
export async function toggleLessonComplete(lessonId) {
  const current = store.progress[lessonId] || { is_completed: false, verse_memorized: false, actions_completed: [] };
  const newState = !current.is_completed;
  
  store.progress[lessonId] = {
    ...current,
    is_completed: newState,
    completed_at: newState ? new Date().toISOString() : null
  };
  saveLocalState();
  notifyStore();

  if (isConfigured && authState.user) {
    await supabase.from('lesson_progress').upsert({
      user_id: authState.user.id,
      lesson_id: lessonId,
      is_completed: newState,
      completed_at: newState ? new Date().toISOString() : null,
      verse_memorized: store.progress[lessonId].verse_memorized,
      actions_completed: store.progress[lessonId].actions_completed,
      updated_at: new Date().toISOString()
    });
  }

  return newState;
}

export async function toggleVerseMemorized(lessonId) {
  const current = store.progress[lessonId] || { is_completed: false, verse_memorized: false, actions_completed: [] };
  const newState = !current.verse_memorized;

  store.progress[lessonId] = {
    ...current,
    verse_memorized: newState
  };
  saveLocalState();
  notifyStore();

  if (isConfigured && authState.user) {
    await supabase.from('lesson_progress').upsert({
      user_id: authState.user.id,
      lesson_id: lessonId,
      is_completed: store.progress[lessonId].is_completed,
      verse_memorized: newState,
      actions_completed: store.progress[lessonId].actions_completed,
      updated_at: new Date().toISOString()
    });
  }

  return newState;
}

export async function toggleActionItem(lessonId, itemIndex) {
  const current = store.progress[lessonId] || { is_completed: false, verse_memorized: false, actions_completed: [] };
  let actions = Array.isArray(current.actions_completed) ? [...current.actions_completed] : [];

  if (actions.includes(itemIndex)) {
    actions = actions.filter(i => i !== itemIndex);
  } else {
    actions.push(itemIndex);
  }

  store.progress[lessonId] = {
    ...current,
    actions_completed: actions
  };
  saveLocalState();
  notifyStore();

  if (isConfigured && authState.user) {
    await supabase.from('lesson_progress').upsert({
      user_id: authState.user.id,
      lesson_id: lessonId,
      is_completed: store.progress[lessonId].is_completed,
      verse_memorized: store.progress[lessonId].verse_memorized,
      actions_completed: actions,
      updated_at: new Date().toISOString()
    });
  }
}

export async function saveReflection(lessonId, responseText, shareWithCohort = false) {
  const currentGroupId = store.currentGroup?.id || null;

  store.reflections[lessonId] = {
    response_text: responseText,
    share_with_cohort: shareWithCohort,
    group_id: currentGroupId,
    updated_at: new Date().toISOString()
  };
  saveLocalState();
  notifyStore();

  if (isConfigured && authState.user) {
    const payload = {
      user_id: authState.user.id,
      lesson_id: lessonId,
      response_text: responseText,
      share_with_cohort: shareWithCohort,
      updated_at: new Date().toISOString()
    };
    if (currentGroupId) {
      payload.group_id = currentGroupId;
    }

    await supabase.from('reflections').upsert(payload);
  }
}

export async function submitQuizAttempt(quizId, answers, score, passed) {
  store.quizAttempts[quizId] = {
    score,
    passed,
    answers,
    submitted_at: new Date().toISOString()
  };
  saveLocalState();
  notifyStore();

  if (isConfigured && authState.user) {
    await supabase.from('quiz_attempts').insert({
      user_id: authState.user.id,
      quiz_id: quizId,
      score,
      passed,
      answers,
      submitted_at: new Date().toISOString()
    });
  }
}

// Helper computations
export function getCourseStats() {
  const total = store.lessons.length;
  let completed = 0;
  let memorized = 0;

  store.lessons.forEach(l => {
    if (store.progress[l.id]?.is_completed) completed++;
    if (store.progress[l.id]?.verse_memorized) memorized++;
  });

  const totalVerses = store.lessons.filter(l => l.verse_ref).length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return {
    totalLessons: total,
    completedLessons: completed,
    totalVerses,
    memorizedVerses: memorized,
    percentage
  };
}
